import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Alert, Switch } from 'antd';
import { saveZohoConfig, getZohoConfig, testZohoConnection, updateZohoConfig } from '../../api/zoho-crm';

const ZohoConfigModal = ({ visible, onClose, token }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (visible) {
      loadExistingConfig();
    }
  }, [visible]);

  const loadExistingConfig = async () => {
    try {
      const response = await getZohoConfig(token);
      if (response.data) {
        form.setFieldsValue({
          clientId: response.data.clientId,
          apiDomain: response.data.apiDomain,
        });
        setIsActive(!!response.data.isActive);
      }
    } catch (error) {
      console.log('No existing config found');
    }
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      const payload = { ...values, isActive };
      const existing = await getZohoConfig(token);
      if (existing?.data) {
        await updateZohoConfig(payload, token);
      } else {
        await saveZohoConfig(payload, token);
      }
      message.success('Zoho CRM configuration saved successfully!');
      setConnectionStatus('success');
      onClose();
    } catch (error) {
      message.error('Failed to save configuration: ' + (error.message || 'Unknown error'));
      setConnectionStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestLoading(true);
    try {
      const response = await testZohoConnection(token);
      if (response.success) {
        message.success('Connection test successful!');
        setConnectionStatus('success');
      } else {
        message.error('Connection test failed');
        setConnectionStatus('error');
      }
    } catch (error) {
      message.error('Connection test failed: ' + (error.message || 'Unknown error'));
      setConnectionStatus('error');
    } finally {
      setTestLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setConnectionStatus(null);
    onClose();
  };

  return (
    <Modal
      title="Zoho CRM Configuration"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Alert
        message="Configure your Zoho CRM API credentials to enable automatic lead synchronization with KPIs."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {connectionStatus === 'success' && (
        <Alert
          message="Connection Successful"
          description="Your Zoho CRM is connected and ready to sync leads."
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {connectionStatus === 'error' && (
        <Alert
          message="Connection Failed"
          description="Please check your credentials and try again."
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          apiDomain: 'https://www.zohoapis.com'
        }}
      >
        <Form.Item label="Enable Zoho CRM" valuePropName="checked">
          <Switch checked={isActive} onChange={(v) => setIsActive(v)} />
        </Form.Item>
        <Form.Item
          label="Client ID"
          name="clientId"
          rules={[{ required: true, message: 'Please enter your Zoho Client ID' }]}
        >
          <Input placeholder="1000.XXXXXXXXXXXXXXXXX" />
        </Form.Item>

        <Form.Item
          label="Client Secret"
          name="clientSecret"
          rules={[{ required: true, message: 'Please enter your Zoho Client Secret' }]}
        >
          <Input.Password placeholder="Enter your client secret" />
        </Form.Item>

        <Form.Item
          label="Refresh Token"
          name="refreshToken"
          rules={[{ required: true, message: 'Please enter your Zoho Refresh Token' }]}
        >
          <Input.Password placeholder="1000.XXXXXXXXXXXXXXXXX" />
        </Form.Item>

        <Form.Item
          label="API Domain"
          name="apiDomain"
        >
          <Input placeholder="https://www.zohoapis.com" />
        </Form.Item>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleTestConnection} 
            loading={testLoading}
            disabled={loading}
          >
            Test Connection
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            disabled={testLoading}
          >
            Save Configuration
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ZohoConfigModal;